import { deleteProjectEntityMap } from './entityMaps';
import { storage } from './storage';
const USER_KEY = 'blindquery_user';
export class DataStore {
    constructor() {
        this.projects = [];
        this.user = null;
        this.byokKeys = [];
    }
    async initialize(existingUser) {
        if (existingUser !== undefined) {
            this.user = existingUser;
        }
        else {
            this.user = (await storage.load(USER_KEY)) || null;
        }
        // Always load data if storage key is available (works for both logged in and anonymous)
        if (storage.hasKey()) {
            // Load projects
            const storedProjects = await storage.load(this.projectsKey(), { encrypted: true });
            this.projects = (storedProjects || []).map(project => ({
                id: project.id,
                name: project.name,
                chats: project.chats.map(chat => ({
                    id: chat.id,
                    name: chat.name,
                    messages: []
                }))
            }));
            // Load BYOK keys
            const storedByok = await storage.load(this.byokKey(), { encrypted: true });
            this.byokKeys = storedByok || [];
        }
        else {
            this.projects = [];
            this.byokKeys = [];
        }
        // Create default project if none exist (for both logged in and anonymous)
        if (this.projects.length === 0 && storage.hasKey()) {
            this.addProject('Default Project');
        }
    }
    projectsKey() {
        return `blindquery_projects_${this.user?.id ?? 'anonymous'}`;
    }
    chatKey(projectId, chatId) {
        return `blindquery_chat_${this.user?.id ?? 'anonymous'}_${projectId}_${chatId}`;
    }
    byokKey() {
        return `blindquery_byok_${this.user?.id ?? 'anonymous'}`;
    }
    buildStoredProjects() {
        return this.projects.map(project => ({
            id: project.id,
            name: project.name,
            chats: project.chats.map(chat => ({
                id: chat.id,
                name: chat.name
            }))
        }));
    }
    async saveProjects() {
        // Save projects for both logged in and anonymous users
        if (!storage.hasKey()) {
            return;
        }
        await storage.save(this.projectsKey(), this.buildStoredProjects(), { encrypted: true });
    }
    async saveChatMessages(projectId, chatId, messages) {
        // Save chat messages for both logged in and anonymous users
        if (!storage.hasKey()) {
            return;
        }
        await storage.save(this.chatKey(projectId, chatId), messages, { encrypted: true });
    }
    async saveByokKeys() {
        await storage.save(this.byokKey(), this.byokKeys, { encrypted: true });
    }
    async loadChatMessages(projectId, chatId) {
        // Load chat messages for both logged in and anonymous users
        if (!storage.hasKey()) {
            return [];
        }
        const messages = await storage.load(this.chatKey(projectId, chatId), { encrypted: true });
        const chat = this.getChat(projectId, chatId);
        if (chat) {
            chat.messages = messages || [];
        }
        return messages || [];
    }
    clearSession() {
        this.projects = [];
        this.byokKeys = [];
        this.user = null;
        void storage.save(USER_KEY, null);
    }
    getUser() {
        return this.user;
    }
    setUser(user) {
        this.user = user;
        void storage.save(USER_KEY, this.user);
    }
    getByokKeys() {
        return this.byokKeys;
    }
    addByokKey(providerId, key) {
        const entry = {
            id: crypto.randomUUID(),
            providerId,
            key,
            createdAt: new Date().toISOString()
        };
        this.byokKeys.unshift(entry);
        void this.saveByokKeys();
        return entry;
    }
    removeByokKey(id) {
        this.byokKeys = this.byokKeys.filter(entry => entry.id !== id);
        void this.saveByokKeys();
    }
    getProjects() {
        return this.projects;
    }
    getProject(id) {
        return this.projects.find(p => p.id === id);
    }
    addProject(name) {
        const newProject = {
            id: crypto.randomUUID(),
            name,
            chats: []
        };
        this.projects.push(newProject);
        void this.saveProjects();
        return newProject;
    }
    updateProject(id, name) {
        const project = this.getProject(id);
        if (project) {
            project.name = name;
            void this.saveProjects();
        }
    }
    deleteProject(id) {
        const project = this.getProject(id);
        if (project) {
            // Delete chat messages for both logged in and anonymous users
            project.chats.forEach(chat => {
                storage.remove(this.chatKey(project.id, chat.id));
            });
        }
        void deleteProjectEntityMap(id);
        this.projects = this.projects.filter(p => p.id !== id);
        void this.saveProjects();
    }
    addChat(projectId, name) {
        const project = this.getProject(projectId);
        if (project) {
            const newChat = {
                id: crypto.randomUUID(),
                name,
                messages: []
            };
            project.chats.push(newChat);
            void this.saveProjects();
            return newChat;
        }
        return undefined;
    }
    deleteChat(projectId, chatId) {
        const project = this.getProject(projectId);
        if (!project) {
            return;
        }
        project.chats = project.chats.filter(chat => chat.id !== chatId);
        // Delete chat messages for both logged in and anonymous users
        storage.remove(this.chatKey(projectId, chatId));
        void this.saveProjects();
    }
    getChat(projectId, chatId) {
        const project = this.getProject(projectId);
        return project?.chats.find(c => c.id === chatId);
    }
    addMessage(projectId, chatId, message) {
        const chat = this.getChat(projectId, chatId);
        if (chat) {
            chat.messages.push(message);
            void this.saveChatMessages(projectId, chatId, chat.messages);
        }
    }
}
export const store = new DataStore();
